import { InterpolationType, ProductCategory } from '@fe/weather';
import { ToggleButton } from '@progress/kendo-react-dateinputs';
import { SliderChangeEvent } from '@progress/kendo-react-inputs';
import React from 'react';
import { useEvent } from '../../../app/contexts/event-bus';
import useDebugFeatures from '../../../hooks/use-debug-features';
import useWeatherService from '../../hooks/use-weather-service';
import AirmetsSigmetsSettings from './sections/AirmetsSigmetsSettings';
import AirportWeatherSettings from './sections/AirportWeatherSettings';
import AirspaceSettings from './sections/AirspaceSettings';
import AnimationSettings from './sections/AnimationSettings';
import IcingSettings from './sections/IcingSettings';
import LightningSettings from './sections/LightningSettings';
import MiscSettings from './sections/MiscSettings';
import RadarSettings from './sections/RadarSettings';
import SatelliteSettings from './sections/SatelliteSettings';
import SigWxSettings from './sections/SigWxSettings';
import TurbulenceSettings from './sections/TurbulenceSettings';
import WeatherSettingsSection from './sections/WeatherSettingsSection';
import WindsSettings from './sections/WindsSettings';
import { SliderWithTooltip } from '../../../app/components/inputs/SliderWithTooltip';

const WeatherSettings = () => {
    const weatherService = useWeatherService();
    const [wmsOpacity, setWmsOpacity] = React.useState(100);
    const [paletteInterpolation, setPaletteInterpolation] = React.useState<InterpolationType>('nearest');
    const [rasterInterpolation, setRasterInterpolation] = React.useState<InterpolationType>('nearest');
    const handleUpdateOpacity = React.useCallback((e: SliderChangeEvent) => setWmsOpacity(e.value), []);

    React.useEffect(() => {
        weatherService?.setWmsOpacity(wmsOpacity);
    }, [weatherService, wmsOpacity]);

    const handleOpenSection = React.useCallback(({ category }: { category: number }) => {
        document.dispatchEvent(new CustomEvent('open-weather-settings-section', { detail: category }));
        self.setTimeout(() => {
            const container = document.querySelector('.layers-card .k-card-body') as HTMLElement | null;
            const section = document.getElementById(`weather-section-${category}`);
            if (!container || !section) {
                return;
            }

            container.scrollBy({
                behavior: 'smooth',
                top: section.offsetTop - container.offsetTop - container.scrollTop
            });
        }, 150);
    }, []);

    useEvent('open-weather-settings', handleOpenSection);

    const debug = useDebugFeatures();

    return (
        <div className="overflow-auto" style={{ width: '365px' }}>
            {debug && (
                <WeatherSettingsSection title="WMS">
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: '15ch' }} className="flex-shrink-0">
                                Opacity ({wmsOpacity.toFixed(0)}%)
                            </div>
                            <SliderWithTooltip min={0} max={100} value={wmsOpacity} className="flex-grow-1" onChange={handleUpdateOpacity} />
                        </div>
                    </div>
                </WeatherSettingsSection>
            )}

            <WeatherSettingsSection title="Animation" category={99 as ProductCategory}>
                <AnimationSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Radar" category={ProductCategory.radar}>
                <RadarSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Satellite" category={ProductCategory.satellite}>
                <SatelliteSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Turbulence" category={ProductCategory.turbulence}>
                <TurbulenceSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Icing" category={ProductCategory.icing}>
                <IcingSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Airmets/Sigmets" category={ProductCategory.airmetsSigmets}>
                <AirmetsSigmetsSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Lightning" category={ProductCategory.lightning}>
                <LightningSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Winds" category={ProductCategory.winds}>
                <WindsSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Airport" category={ProductCategory.airport}>
                <AirportWeatherSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Airspace" category={ProductCategory.airspace}>
                <AirspaceSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="Miscellaneous" category={ProductCategory.miscellaneous}>
                <MiscSettings />
            </WeatherSettingsSection>

            <WeatherSettingsSection title="SigWx" category={ProductCategory.sigWx}>
                <SigWxSettings />
            </WeatherSettingsSection>

            {debug && (
                <WeatherSettingsSection title="Interpolation">
                    <div className="px-3">
                        <div className="d-flex gap-2 align-items-center">
                            <div className="flex-grow-1">Palette interpolation</div>
                            <ToggleButton
                                togglable={true}
                                selected={paletteInterpolation === 'linear'}
                                fillMode={'outline'}
                                onClick={() => setPaletteInterpolation('linear')}>
                                Linear
                            </ToggleButton>
                            <ToggleButton
                                togglable={true}
                                selected={paletteInterpolation === 'nearest'}
                                fillMode={'outline'}
                                onClick={() => setPaletteInterpolation('nearest')}>
                                Nearest
                            </ToggleButton>
                        </div>
                        <div className="d-flex gap-2 align-items-center pt-2">
                            <div className="flex-grow-1">Raster interpolation</div>
                            <ToggleButton
                                togglable={true}
                                selected={rasterInterpolation === 'linear'}
                                fillMode={'outline'}
                                onClick={() => setRasterInterpolation('linear')}>
                                Linear
                            </ToggleButton>
                            <ToggleButton
                                togglable={true}
                                selected={rasterInterpolation === 'nearest'}
                                fillMode={'outline'}
                                onClick={() => setRasterInterpolation('nearest')}>
                                Nearest
                            </ToggleButton>
                        </div>
                    </div>
                </WeatherSettingsSection>
            )}
        </div>
    );
};

export default WeatherSettings;
